import React from "react";

// Brand palette (kept here so the canvas has no external style deps).
const BRAND = {
  forest: "#114232",
  sage: "#94A596",
  cream: "#FAF7F1",
  bone: "#F2EEE3",
  ink: "#0C2A20",
  terracotta: "#E07856",
  terracottaDeep: "#B8593B",
};

// Format specs — width, height, safe padding.
export const FORMATS = {
  square: { w: 1080, h: 1080, label: "Square 1080×1080", use: "Facebook / Instagram / eBay" },
  portrait: { w: 1080, h: 1350, label: "Portrait 1080×1350", use: "Instagram feed / Facebook" },
  story: { w: 1080, h: 1920, label: "Story 1080×1920", use: "Stories / Reels / TikTok" },
  landscape: { w: 1200, h: 628, label: "Landscape 1200×628", use: "Facebook link preview" },
};

// --- Small helpers -----------------------------------------------------------

const Bubbles = ({ items }) =>
  items.map((b, i) => (
    <div
      key={i}
      style={{
        position: "absolute",
        left: b.x,
        top: b.y,
        width: b.s,
        height: b.s,
        borderRadius: b.s / 2,
        background: b.c || BRAND.sage,
        opacity: b.o,
      }}
    />
  ));

const MattedPhoto = ({ padding = 14, radius = 22, imgRadius = 14, height, width, badge }) => (
  <div
    style={{
      position: "relative",
      width,
      height,
      background: BRAND.cream,
      borderRadius: radius,
      padding,
      border: `1px solid ${BRAND.sage}`,
      boxShadow:
        "0 30px 60px rgba(17,66,50,0.35), inset 0 1px 0 rgba(255,255,255,0.9)",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: imgRadius,
        overflow: "hidden",
        boxShadow: "inset 0 0 0 1px rgba(17,66,50,0.12)",
      }}
    >
      <img
        src="/images/machine-hero.jpg"
        alt=""
        crossOrigin="anonymous"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </div>
    {badge && (
      <div
        style={{
          position: "absolute",
          left: padding + 12,
          bottom: padding + 12,
          background: "rgba(17,66,50,0.88)",
          color: BRAND.cream,
          padding: "8px 14px",
          borderRadius: 999,
          fontSize: badge.fs || 13,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ color: BRAND.terracotta, fontSize: 12 }}>●</span>
        {badge.text}
      </div>
    )}
  </div>
);

// Circular "portrait medallion" of a clean happy dog — decorative,
// placed only in safe negative space so it never fights the machine or copy.
const DogBadge = ({ src, size, ringColor = "#FAF7F1", ringWidth = 6, rotate = 0, style }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      overflow: "hidden",
      background: ringColor,
      padding: ringWidth,
      boxShadow: "0 14px 30px rgba(17,66,50,0.35)",
      transform: `rotate(${rotate}deg)`,
      ...style,
    }}
  >
    <img
      src={src}
      alt=""
      crossOrigin="anonymous"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "50%",
        display: "block",
      }}
    />
  </div>
);

const RevenueStrip = ({ scenarios, width, fontScale = 1 }) => (
  <div
    style={{
      width,
      background: BRAND.terracotta,
      color: BRAND.cream,
      borderRadius: 20,
      padding: 18 * fontScale,
      boxShadow: "0 20px 40px rgba(224,120,86,0.32)",
    }}
  >
    <div
      style={{
        fontSize: 13 * fontScale,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: "#FFE7DA",
        fontWeight: 700,
        marginBottom: 12 * fontScale,
      }}
    >
      Net revenue potential
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 * fontScale }}>
      {scenarios.map((s, i) => (
        <div
          key={i}
          style={{
            background: "rgba(255,255,255,0.14)",
            borderRadius: 12,
            padding: `${10 * fontScale}px ${8 * fontScale}px`,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 12 * fontScale,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#FFE7DA",
              whiteSpace: "nowrap",
            }}
          >
            {s.d} washes/day
          </div>
          <div
            style={{
              fontFamily: "Chewy, system-ui",
              fontSize: 34 * fontScale,
              lineHeight: 1,
              marginTop: 6 * fontScale,
              whiteSpace: "nowrap",
            }}
          >
            {s.net}
          </div>
          <div
            style={{
              fontSize: 10 * fontScale,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#FFE7DA",
              opacity: 0.85,
              marginTop: 4 * fontScale,
            }}
          >
            /mo
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Headline = ({ text, size, color = BRAND.forest, accentLast = true }) => {
  const lines = text.split("\n");
  return (
    <div style={{ fontFamily: "Chewy, system-ui", fontSize: size, lineHeight: 1.02, color }}>
      {lines.map((ln, i) => (
        <div key={i} style={{ color: accentLast && i === lines.length - 1 ? BRAND.terracotta : color }}>
          {ln}
        </div>
      ))}
    </div>
  );
};

// --- Format renderers --------------------------------------------------------

const Square = ({ a }) => (
  <div style={frameStyles(FORMATS.square)}>
    <Bubbles
      items={[
        { s: 340, x: -120, y: -140, o: 0.18 },
        { s: 220, x: 900, y: 780, o: 0.16 },
        { s: 140, x: 850, y: -40, c: BRAND.terracotta, o: 0.12 },
      ]}
    />
    {/* Header */}
    <div
      style={{
        position: "absolute",
        top: 40,
        left: 48,
        right: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 3,
      }}
    >
      <BrandMark tagline={a.tagline} />
      <SpaceChip text={a.spaceChip} />
    </div>

    {/* Body */}
    <div
      style={{
        position: "absolute",
        top: 190,
        left: 48,
        right: 48,
        bottom: 260,
        display: "flex",
        gap: 32,
        alignItems: "center",
        zIndex: 3,
      }}
    >
      <MattedPhoto width={500} height={416} badge={{ text: "Full footprint · 2m²" }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ color: BRAND.terracottaDeep, fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>
          {a.eyebrow}
        </div>
        <Headline text={a.headline} size={58} />
        <div style={{ marginTop: 16, fontSize: 20, lineHeight: 1.4, color: "#243B33", maxWidth: 460 }}>
          {a.subheadline}
        </div>
      </div>
    </div>

    {/* Revenue strip + footer */}
    <div style={{ position: "absolute", bottom: 130, left: 48, right: 48, zIndex: 3 }}>
      <RevenueStrip scenarios={a.scenarios} width={"100%"} />
    </div>
    <Footer a={a} />
  </div>
);

const Portrait = ({ a }) => (
  <div style={frameStyles(FORMATS.portrait)}>
    <Bubbles
      items={[
        { s: 380, x: -140, y: -160, o: 0.18 },
        { s: 260, x: 880, y: 1150, o: 0.16 },
        { s: 160, x: 900, y: 220, c: BRAND.terracotta, o: 0.12 },
      ]}
    />
    <div style={{ position: "absolute", top: 60, left: 60, right: 60, zIndex: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <BrandMark tagline={a.tagline} />
      <SpaceChip text={a.spaceChip} />
    </div>
    <div style={{ position: "absolute", top: 180, left: 60, right: 60, zIndex: 3, textAlign: "center" }}>
      <div style={{ color: BRAND.terracottaDeep, fontSize: 13, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700 }}>
        {a.eyebrow}
      </div>
    </div>
    <div style={{ position: "absolute", top: 230, left: 60, right: 60, zIndex: 3 }}>
      <MattedPhoto width={960} height={800} badge={{ text: "Full footprint · 2m²" }} />
    </div>
    <div style={{ position: "absolute", bottom: 150, left: 60, right: 60, zIndex: 3 }}>
      <RevenueStrip scenarios={a.scenarios} width={"100%"} fontScale={0.95} />
    </div>
    <Footer a={a} />
  </div>
);

const Story = ({ a }) => (
  <div style={frameStyles(FORMATS.story)}>
    <Bubbles
      items={[
        { s: 460, x: -180, y: -200, o: 0.18 },
        { s: 320, x: 820, y: 1550, o: 0.16 },
        { s: 200, x: 880, y: 260, c: BRAND.terracotta, o: 0.14 },
        { s: 130, x: 70, y: 1750, o: 0.18 },
      ]}
    />
    <div style={{ position: "absolute", top: 80, left: 70, right: 70, zIndex: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <BrandMark tagline={a.tagline} big />
      <SpaceChip text={a.spaceChip} big />
    </div>
    <div style={{ position: "absolute", top: 260, left: 70, right: 70, zIndex: 3 }}>
      <MattedPhoto width={940} height={784} badge={{ text: "Full footprint · 2m²", fs: 14 }} />
    </div>
    <div style={{ position: "absolute", top: 1120, left: 70, right: 70, zIndex: 3 }}>
      <div style={{ color: BRAND.terracottaDeep, fontSize: 14, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, marginBottom: 18 }}>
        {a.eyebrow}
      </div>
      <Headline text={a.headline} size={92} />
    </div>
    <div style={{ position: "absolute", bottom: 260, left: 70, right: 70, zIndex: 3 }}>
      <RevenueStrip scenarios={a.scenarios} width={"100%"} fontScale={1.25} />
    </div>
    <StoryFooter a={a} />
  </div>
);

const Landscape = ({ a }) => (
  <div style={frameStyles(FORMATS.landscape)}>
    <Bubbles
      items={[
        { s: 260, x: -90, y: -100, o: 0.18 },
        { s: 180, x: 1080, y: 480, o: 0.16 },
        { s: 120, x: 1050, y: -30, c: BRAND.terracotta, o: 0.14 },
      ]}
    />
    <div style={{ position: "absolute", inset: 0, padding: 40, display: "flex", gap: 32, alignItems: "center", zIndex: 3 }}>
      <MattedPhoto width={540} height={450} badge={{ text: "Full footprint · 2m²" }} padding={12} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
        <div>
          <BrandMark tagline={a.tagline} />
          <div style={{ marginTop: 16 }}>
            <div style={{ color: BRAND.terracottaDeep, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
              {a.eyebrow}
            </div>
            <Headline text={a.headline} size={40} />
          </div>
        </div>
        <div>
          <RevenueStrip scenarios={a.scenarios} width={"100%"} fontScale={0.7} />
          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", color: BRAND.forest, fontSize: 13 }}>
            <div style={{ fontFamily: "Chewy, system-ui", fontSize: 22 }}>{a.website}</div>
            <div>{a.contactEmail}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// --- Building blocks reused across formats ----------------------------------

const BrandMark = ({ tagline, big }) => (
  <div style={{ display: "flex", alignItems: "center", gap: big ? 22 : 16 }}>
    <img
      src="/brand/logo-circle.png"
      alt=""
      crossOrigin="anonymous"
      style={{ width: big ? 108 : 80, height: big ? 108 : 80, borderRadius: 999, objectFit: "cover" }}
    />
    <div>
      <div style={{ fontFamily: "Chewy, system-ui", fontSize: big ? 40 : 32, lineHeight: 1, color: BRAND.forest }}>
        Messy Pups
      </div>
      <div style={{ fontSize: big ? 13 : 11, letterSpacing: "0.28em", textTransform: "uppercase", color: BRAND.terracottaDeep, marginTop: 6, fontWeight: 700 }}>
        {tagline}
      </div>
    </div>
  </div>
);

const SpaceChip = ({ text, big }) => (
  <div
    style={{
      background: BRAND.cream,
      color: BRAND.forest,
      padding: big ? "14px 22px" : "10px 16px",
      borderRadius: 999,
      border: `1px solid ${BRAND.sage}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      boxShadow: "0 8px 20px rgba(17,66,50,0.12)",
    }}
  >
    <span style={{ color: BRAND.terracotta, fontSize: big ? 16 : 13 }}>●</span>
    <span style={{ fontFamily: "Chewy, system-ui", fontSize: big ? 28 : 22, lineHeight: 1 }}>{text}</span>
  </div>
);

const Footer = ({ a }) => (
  <div
    style={{
      position: "absolute",
      bottom: 40,
      left: 48,
      right: 48,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      zIndex: 3,
    }}
  >
    <div>
      <div style={{ fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: BRAND.terracottaDeep, fontWeight: 700, marginBottom: 4 }}>
        Enquire — no obligation
      </div>
      <div style={{ fontFamily: "Chewy, system-ui", fontSize: 26, lineHeight: 1.1, color: BRAND.forest }}>
        {a.contactEmail}
      </div>
    </div>
    <div style={{ textAlign: "right", color: BRAND.forest }}>
      <div style={{ fontFamily: "Chewy, system-ui", fontSize: 26, lineHeight: 1 }}>{a.website}</div>
    </div>
  </div>
);

const StoryFooter = ({ a }) => (
  <div style={{ position: "absolute", bottom: 60, left: 70, right: 70, zIndex: 3, textAlign: "center" }}>
    <div
      style={{
        display: "inline-block",
        background: BRAND.forest,
        color: BRAND.cream,
        padding: "22px 36px",
        borderRadius: 999,
        boxShadow: "0 20px 40px rgba(17,66,50,0.35)",
      }}
    >
      <div style={{ fontSize: 14, letterSpacing: "0.28em", textTransform: "uppercase", color: BRAND.sage, fontWeight: 700, marginBottom: 4 }}>
        Enquire — no obligation
      </div>
      <div style={{ fontFamily: "Chewy, system-ui", fontSize: 40, lineHeight: 1 }}>{a.contactEmail}</div>
    </div>
    <div style={{ marginTop: 18, fontFamily: "Chewy, system-ui", fontSize: 30, color: BRAND.forest }}>
      {a.website}
    </div>
  </div>
);

const frameStyles = (fmt) => ({
  width: fmt.w,
  height: fmt.h,
  background: BRAND.bone,
  color: BRAND.forest,
  fontFamily: "Carlito, system-ui, sans-serif",
  position: "relative",
  overflow: "hidden",
  boxSizing: "border-box",
});

// --- Main component ---------------------------------------------------------

const SocialAdvertCanvas = React.forwardRef(({ format, advert, scale = 1 }, ref) => {
  const fmt = FORMATS[format];
  const Renderer = { square: Square, portrait: Portrait, story: Story, landscape: Landscape }[format];

  return (
    <div
      ref={ref}
      data-testid={`social-canvas-${format}`}
      style={{
        width: fmt.w,
        height: fmt.h,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      }}
    >
      <Renderer a={advert} />
    </div>
  );
});

SocialAdvertCanvas.displayName = "SocialAdvertCanvas";
export default SocialAdvertCanvas;
